import { useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { resolveDisplayName, resolveUserInitials } from '@levelup/central-auth';
import { useCentralAuth } from '../../hooks/useCentralAuth';
import { WorkspaceSelector } from '../WorkspaceSelector';
import { AppSwitcher } from '../AppSwitcher';
import clsx from 'clsx';
import { DevAccessDebugPanel } from '../dev/DevAccessDebugPanel';
import { useGetCapabilitiesQuery } from '../../store/api/capabilitiesApi';

const navigation = [
  { name: 'Home', href: '/dashboard', icon: HomeIcon },
  { name: 'Practice', href: '/practice', icon: MicIcon },
  // RC19: the skills runs are UNSCORED practice until an evidence-based
  // evaluator exists (AuthorityScoringService) — named for what they are.
  { name: 'Skill practice', href: '/skills', icon: ClipboardIcon },
  { name: 'Marketplace', href: '/marketplace', icon: StoreIcon },
  { name: 'Profile', href: '/profile', icon: UserIcon },
];

export function MainLayout() {
  const { user: centralUser, isAuthenticated: isCentralAuthenticated, logout: centralLogout } = useCentralAuth();
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);
  // The Content Library is offered only when the server has real semantic
  // retrieval (GET /capabilities rag: true) — RC10.
  const { data: serverCaps } = useGetCapabilitiesQuery();
  const contentLibraryAvailable = serverCaps?.rag === true;

  const displayName = resolveDisplayName(centralUser);
  const initials = resolveUserInitials(centralUser);
  const handleLogout = centralLogout;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <span className="text-xl font-bold text-primary-600">BeHeard</span>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              {navigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    clsx(
                      'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    )
                  }
                >
                  {item.name}
                </NavLink>
              ))}
            </nav>

            {/* User menu */}
            <div className="flex items-center gap-4">
              {/* App switcher (Central auth only, multiple suite apps) */}
              {isCentralAuthenticated && <AppSwitcher />}
              {/* Workspace selector (Central auth only, multiple workspaces) */}
              {isCentralAuthenticated && <WorkspaceSelector />}

              {/* Dropdown menu for secondary nav */}
              <div className="relative group hidden sm:block">
                <button className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700">
                  More
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  <NavLink to="/coaching" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    Coach
                  </NavLink>
                  <NavLink to="/learn" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    Learn
                  </NavLink>
                  <NavLink to="/my/timeline" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    Timeline
                  </NavLink>
                  <div className="border-t border-gray-200 my-1" />
                  <NavLink to="/referrals" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    Referrals
                  </NavLink>
                  {contentLibraryAvailable && (
                    <NavLink to="/content" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Content Library
                    </NavLink>
                  )}
                  <NavLink to="/usage" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    Usage & Limits
                  </NavLink>
                  <NavLink to="/settings" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    Settings
                  </NavLink>
                  {/* DEV-ONLY: Access Debug Panel */}
                  {import.meta.env.DEV && (
                    <>
                      <div className="border-t border-gray-200 my-1" />
                      <button
                        onClick={() => setDebugPanelOpen(true)}
                        className="block w-full text-left px-4 py-2 text-sm text-amber-700 hover:bg-amber-50"
                      >
                        🛠️ Debug Access
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                {/* Avatar with initials fallback (shared identity helpers) */}
                <span
                  aria-hidden
                  className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-semibold"
                  title={displayName}
                >
                  {initials}
                </span>
                <span className="text-sm text-gray-600">{displayName}</span>
              </div>
              <button
                onClick={handleLogout}
                className="text-sm text-gray-500 hover:text-gray-700 hidden sm:inline"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>

      {/* Mobile navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 safe-area-inset-bottom">
        <div className="flex justify-around">
          <NavLink
            to="/dashboard"
            className={({ isActive }) =>
              clsx(
                'flex flex-col items-center py-2 px-2 text-xs',
                isActive ? 'text-primary-600' : 'text-gray-500'
              )
            }
          >
            <HomeIcon className="w-5 h-5" />
            <span className="mt-1">Home</span>
          </NavLink>
          <NavLink
            to="/practice"
            className={({ isActive }) =>
              clsx(
                'flex flex-col items-center py-2 px-2 text-xs',
                isActive ? 'text-primary-600' : 'text-gray-500'
              )
            }
          >
            <MicIcon className="w-5 h-5" />
            <span className="mt-1">Practice</span>
          </NavLink>
          <NavLink
            to="/skills"
            className={({ isActive }) =>
              clsx(
                'flex flex-col items-center py-2 px-2 text-xs',
                isActive ? 'text-primary-600' : 'text-gray-500'
              )
            }
          >
            <ClipboardIcon className="w-5 h-5" />
            <span className="mt-1 whitespace-nowrap">Skill practice</span>
          </NavLink>
          <NavLink
            to="/marketplace"
            className={({ isActive }) =>
              clsx(
                'flex flex-col items-center py-2 px-2 text-xs',
                isActive ? 'text-primary-600' : 'text-gray-500'
              )
            }
          >
            <StoreIcon className="w-5 h-5" />
            <span className="mt-1">Marketplace</span>
          </NavLink>
          <NavLink
            to="/profile"
            className={({ isActive }) =>
              clsx(
                'flex flex-col items-center py-2 px-2 text-xs',
                isActive ? 'text-primary-600' : 'text-gray-500'
              )
            }
          >
            <UserIcon className="w-5 h-5" />
            <span className="mt-1">Profile</span>
          </NavLink>
        </div>
      </nav>

      {/* DEV-ONLY: Access Debug Panel */}
      <DevAccessDebugPanel isOpen={debugPanelOpen} onClose={() => setDebugPanelOpen(false)} />
    </div>
  );
}

// Icon components (simple placeholders)
function MicIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
    </svg>
  );
}

function StoreIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function HomeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  );
}

function ClipboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}
